<template>
    <div>
        <div class="flex flex-row">
            <div v-if="creatable && canAddObject" class="relative inline-block">
                <button
                    :disabled="disabled"
                    class="border-0 bg-0 flex flex-row items-center"
                    :class="disabled ? 'text-gray-600 cursor-not-allowed' : 'text-blue-600'"
                    type="button"
                    @click="openNew"
                >
                    + {{createText}}
                    <span class="text-gray-400 ml-3" v-if="!modelData && !listData">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="3"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </span>
                </button>
                <transition name="dropdown">
                    <div
                        v-show="newOpen"
                        class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg z-10"
                    >
                        <div
                            class="rounded-md bg-white ring-1 ring-black ring-opacity-5"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="options-menu"
                        >
                            <div class="py-1">
                                <a
                                    v-for="type in Object.keys(contentTypes)"
                                    :key="`contentType-${type}`"
                                    @click="() => openSlide(type, 'Form')"
                                    class="cursor-pointer block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                    role="menuitem"
                                >
                                    {{ type }}
                                </a>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <div :class="['relative', 'inline-block', creatable ? 'ml-5' : null]" v-if="linkable && canAddObject">
                <button
                    :disabled="disabled"
                    class="border-0 bg-0 flex flex-row items-center"
                    :class="disabled ? 'text-gray-600 cursor-not-allowed' : 'text-blue-600'"
                    type="button"
                    @click="openLink"
                >
                    <svg
                        v-if="!hideLinkIcon"
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                    </svg>
                    <span class="inline-block ml-1"
                        >{{linkText}}</span
                    >
                    <span class="text-gray-400 ml-3" v-if="!modelData && !listData">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="3"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </span>
                </button>
                <transition name="dropdown">
                    <div
                        v-show="linkOpen"
                        class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg z-10"
                    >
                        <div
                            class="rounded-md bg-white ring-1 ring-black ring-opacity-5"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="options-menu"
                        >
                            <div class="py-1">
                                <a
                                    v-for="type in Object.keys(contentTypes)"
                                    :key="`contentTypeLink-${type}`"
                                    @click="() => openSlide(type, 'List')"
                                    class="cursor-pointer block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                    role="menuitem"
                                >
                                    {{ type }}
                                </a>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
        <div v-if="objects.length > 0" class="text-gray-500 my-2">
            Referenced:
            <div class="space-y-2">
                <draggable
                    v-model="objects"
                    @input="data => $emit('input', data)"
                    handle=".handle"
                    :disabled="!draggable"
                    @choose="froalaStore.disableFroala = true"
                    @unchoose="froalaStore.disableFroala = false"
                >
                    <div
                        v-for="(object, index) in showAll ? objects : objects.slice(0, maxRows)"
                        :key="`${component}-${object.id}-${index}`"
                        class="py-2 rounded hover:bg-gray-100 flex flex-row items-center sm:w-1/2"
                        :class="checkHighlight(object) ? 'bg-red-100 hover:bg-red-200' : ''"
                        :title="getHighlightTitle(object)"
                    >
                        <div v-if="numbered" class="w-10">{{ index + 1 }}</div>
                        <span v-if="draggable && objects.length > 1"
                            class="handle pr-1 text-gray-400 hover:text-gray-600 cursor-pointer transition duration-150 transform hover:scale-105"
                              title="Drag to reorder items">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 17l-4 4m0 0l-4-4m4 4V3" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7l4-4m0 0l4 4m-4-4v18" />
                            </svg>
                        </span>
                        <span
                            v-if="deletable"
                            @click="removeObject(index)"
                            class="text-gray-400 hover:text-gray-600 cursor-pointer transition duration-150 transform hover:scale-105"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </span>
                        <span
                            v-if="shouldOpenNewWindow"
                            class="text-gray-400 hover:text-gray-600 cursor-pointer ml-1 transition duration-150 transform hover:scale-105"
                        >
                            <a :href="generateObjectEditRoute(object).edit_route" target="_blank">
                                <svg
                                    class="h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                    />
                                </svg>
                            </a>
                        </span>
                        <span
                            v-if="editable"
                            @click="() => openSlide(model, 'Form', index)"
                            class="text-gray-400 hover:text-gray-600 cursor-pointer ml-1 transition duration-150 transform hover:scale-105"
                        >
                                <svg
                                    class="h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                </svg>
                        </span>
                        <span v-if="debugStore.debugMode && object.id" class="font-mono ml-4">
                            [{{ model }}:{{ object.id }}]
                        </span>
                        <span class="text-gray-700 ml-4">
                            <div v-if="model === 'Media'">
                                <img
                                    :src="object[
                                        contentTypes[model]
                                            ? contentTypes[model].columnSelector
                                            : null
                                    ]"
                                    alt=""
                                    class="h-20 w-auto"
                                />
                            </div>
                            <div v-if="model !== 'Media'">
                                {{
                                    getItemLabel(object)
                                }}
                            </div>
                        </span>
                    </div>
                </draggable>
                <div v-if="objects.length> maxRows">
                    <button @click.prevent="toggleItems" type="button" class="rounded-md text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-2 focus:outline-none">
                        {{ showAll ? "Collapse" : "Show all" }}
                    </button>
                </div>
            </div>
        </div>
        <input
            v-for="(object, key) in objects"
            :key="`${inputName}_${key}`"
            type="hidden"
            :name="`${inputName}[]`"
            :value="object.id"
        />
        <slide-over
            draggable="false"
            :empty-message="emptyMessage"
            :content-types="contentTypeFilters"
            @close="slideOpen = false"
            :full-width="fullWidth"
            :show-select-all="showSelectAll"
            :is-open="slideOpen"
            :multiple="true"
            :selected="this.objects.map((obj) => obj.id)"
            :selected-objects="this.objects"
            :component="component"
            :formName="baseFormName"
            :excluded-ids="excludedIds"
            :object="currentObject"
            :model="model"
            :language="language"
            :routeKeyName="routeKeyName"
            :label="
                contentTypes[model] ? contentTypes[model].columnSelector : null
            "
            :formRoute="currentObject ? currentObject.updateRoute : formRoute"
            :listRoute="
                contentTypes[model] ? contentTypes[model].listRoute : null
            "
            :query="query"
            :order="order"
            @created="objectCreated"
            @selected="objectSelected"
            @deselect-all="deselectAll"
            @select-all="(selectAllObjects) => selectAll(selectAllObjects)"
            @list-loaded="listLoaded"
            :list-data="listData"
            :max-items="maxItems"
            :pre-filled="preFilled"
            :with-docs="withDocs"
            :index="index"
        />
        <div v-if="error" class="mt-3">
            <error :error="error" />
        </div>
    </div>
</template>
<script>
import draggable from 'vuedraggable';
import reference from "../mixins/reference";
import {useDebugStore} from "../stores/debug";
import {useFroalaStore} from "../stores/froala";

export default {
    name: "MultiReference",
    mixins: [reference],
    components: {
        draggable,
    },
    props: {
        fieldName: String,
        value: Array,
        query: Object,
        order: Object,
        hideLinkIcon: {
            type: Boolean,
            default: false,
        },
        modelData: String,
        listData: Array,
        language: Object,
        contentTypeFilters: Array,
        fullWidth: {
            type: Boolean,
            default: false,
        },
        showSelectAll: {
            type: Boolean,
            default: false,
        },
        linkable: {
            type: Boolean,
            default: true,
            required: false,
        },
        numbered: {
            type: Boolean,
            default: false,
            required: false,
        },
        editable: {
            type: Boolean,
            default: false,
            required: false
        },
        deletable: {
            type: Boolean,
            default: true,
            required: false
        },
        forceDelete: {
            type: Boolean,
            default: false,
            required: false,
        },
        twoStepCreate: Boolean,
        excludedIds: Array,
        maxItems: Number,
        maxItemMessage: String,
        creatable: {
            type: Boolean,
            default: true,
        },
        highlight: {
            type: Array,
            default: () => [],
        },
        preFilled: {
            type: Object,
            required: false,
        },
        error: {
            type: String,
            required: false,
        },
        disabled:{
            type: Boolean,
            default: false
        },
        emptyMessage: {
            type: String,
            required: false
        },
        withDocs: {
            type: Array,
            required: false
        },
        createText: {
            type: String,
            required: false,
            default: 'Create and link a new entry'
        },
        linkText: {
            type: String,
            required: false,
            default: 'Link existing entries'
        },
        draggable: {
            type: Boolean,
            default: true
        },
        shouldOpenNewWindow: {
            type: Boolean,
            required: false,
            default: false
        },
    },
    data() {
        return {
            listCount: 0,
            newOpen: false,
            isCreating: false,
            linkOpen: false,
            slideOpen: false,
            model: this.modelData || null,
            objects: this.value || [],
            visibleObjects: [],
            component: null,
            currentObject: null,
            formComponent: "",
            maxRows: 5,
            showAll: false,
            index: null,
            debugStore: useDebugStore(),
            froalaStore: useFroalaStore(),
        }
    },
    created() {
        if (this.editable) {
            this.updateObjectsUpdateRoute();
        }
        if (this.deletable && this.forceDelete) {
            this.updateObjectsDeleteRoute();
        }
    },
    methods: {
        deselectAll(){
            this.objects = [];
            this.selectionUpdated();
        },
        selectAll(selectAllObjects){
            this.objects = selectAllObjects;
            this.updateObjectsUpdateRoute();
            this.selectionUpdated();
        },
        openSlide(model, type, index = null) {
            if (index !== null) {
                this.isCreating = false
            }

            this.index = index
            this.currentObject = this.objects?.[index] || null
            this.model = model
            this.component = type === 'Form' ? this.formName : 'ModelList'
            this.slideOpen = true
            this.newOpen = false
            this.linkOpen = false
        },
        openLink() {
            if(this.disabled) return;
            this.isCreating = true;
            if (this.modelData || this.listData) {
                this.openSlide(this.model, "Link");
                return;
            }
            this.linkOpen = !this.linkOpen;
            this.newOpen = false;
        },
        openNew() {
            if(this.disabled) return;
            this.isCreating = true;
            if (this.modelData) {
                this.openSlide(this.model, "Form");
                return;
            }
            this.newOpen = !this.newOpen;
            this.linkOpen = false;
        },
        listLoaded (count) {
            this.listCount = count;
            this.selectionUpdated();
        },
        selectionUpdated () {
            this.$emit('selection-updated', {
                list: this.listCount,
                selected: this.objects.length,
            });
        },
        objectCreated({object, component}) {

            if (!this.canAddObject && !this.isDeselecting(object)) {
                this.maximumItemsReached()

                return
            }

            object = this.generateObjectUpdateRoute(object)

            this.addToObjects(object, true)

            if (component !== 'ModelList') {
                this.slideOpen = false
            }

            this.selectionUpdated()
        },
        objectSelected({object, component}) {

            if (!this.canAddObject && !this.isDeselecting(object)) {
                this.maximumItemsReached()

                return
            }

            object = this.generateObjectUpdateRoute(object)

            this.addToObjects(object, false)

            if (component !== 'ModelList') {
                this.slideOpen = false
            }

            this.selectionUpdated()
        },
        isDeselecting(object) {
            const slimlineObjects = this.objects.map((object) => object.id);
            const index = slimlineObjects.indexOf(object.id);
            return index >= 0;
        },
        checkHighlight(object){
            return this.highlight.map(highlight => highlight.id).includes(object.id);
        },
        getHighlightTitle(object){
            const highlightItem = this.highlight.filter(highlight => highlight.id === object.id)
            return highlightItem[0]?.title ? highlightItem[0]?.title : '';
        },
        removeObject(index) {
            if(this.forceDelete){
                const canDelete = confirm('Are you sure you want to delete this? It will be removed on confirm');
                if(canDelete){
                    axios.delete(this.objects[index].deleteRoute)
                    .then(response => {
                        this.objects.splice(index, 1);
                        this.$emit('deleted');
                    })
                    .catch(error => {
                        console.log(error);
                        this.$notify({
                            group: "formError",
                            title: `Sorry there was a problem deleting this.`,
                        });
                    })
                }
            }else{
                this.objects.splice(index, 1);
            }
            this.selectionUpdated();
        },
        addToObjects(object, replaceIfExists) {
            const slimlineObjects = this.objects.map((object) => object.id);
            const index = slimlineObjects.indexOf(object.id);
            if (index !== -1) {
                if (replaceIfExists) {
                    this.objects.splice(index, 1, object);
                }
                else {
                    this.objects.splice(index, 1);
                }

                this.selectionUpdated();
                return;
            }
            this.updateObjectsUpdateRoute();

            this.objects.push(object);
        },
        updateObjectsUpdateRoute() {
            if (this.objects.length) {
                this.objects = this.objects.map(this.generateObjectUpdateRoute);
            }
        },
        updateObjectsDeleteRoute() {
            if (this.objects.length) {
                this.objects = this.objects.map(this.generateObjectDeleteRoute);
            }
        },
        maximumItemsReached() {
            this.$notify({
                group: 'formError',
                title: this.maxItemMessage || `You can only select ${this.maxItems} items`,
            })
        },
        toggleItems() {
            const objects = [...this.objects];
            this.objects = objects.map((object, index) => {
                object.weight = index;
                return object;
            });

            this.showAll = !this.showAll;
            this.$emit('input', this.objects)
        }
    },
    computed: {
        formName() {
            return this.twoStepCreate && this.language.id !== 1 && this.isCreating ? `two-step-create` : `${this.componentName}-form`;
        },
        baseFormName() {
            return `${this.componentName}-form`;
        },
        componentName() {
            return this.model?.toLowerCase().split(" ").join("-");
        },
        inputName() {
            return this.fieldName || `${this.model.toLowerCase()}_id`;
        },
        formRoute() {
            if (this.objects.length > 0 && this.isCreating == false) {
                return this.contentTypes[this.model]
                    ? this.contentTypes[this.model].updateRoute
                    : null;
            }
            return this.contentTypes[this.model]
                ? this.contentTypes[this.model].formRoute
                : null;
        },
        canAddObject() {
            if (this.maxItems) {
                if (this.objects.length >= this.maxItems) {
                    return false
                }
            }

            return true
        },
        routeKeyName() {
            return this.contentTypes[this.model]?.routeKeyName || 'id';
        },
    },
    watch: {
        objects(newValue) {
            this.$emit("input", newValue);
        },
        value(newValue) {
            this.objects = newValue;
        },
        modelData(newValue) {
            this.model = newValue;
        },
    },
};
</script>
